import { DateTime, DateTimeOptions } from 'luxon';
import { Timezone } from '~/types/models';
import { elapsed } from './misc';

export enum TimeFormat {
  AM = 'am',
  PM = 'pm',
}
export interface ParseUtcDateOptions {
  parseNumberAs: 'millis' | 'seconds';
}

export function parseUtcDate(
  input: string | number | Date,
  options?: Partial<ParseUtcDateOptions>
): DateTime;
export function parseUtcDate(
  input: undefined | null,
  options?: Partial<ParseUtcDateOptions>
): undefined;
export function parseUtcDate(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any,
  options?: Partial<ParseUtcDateOptions>
): DateTime | undefined {
  const dtOptions: DateTimeOptions = {
    zone: 'utc',
  };

  if (typeof input === 'string') {
    const sqlDate = DateTime.fromSQL(input, dtOptions);

    if (sqlDate.invalidReason) {
      return DateTime.fromISO(input, dtOptions);
    } else {
      return sqlDate;
    }
  }

  if (typeof input === 'number') {
    switch (options?.parseNumberAs ?? 'seconds') {
      case 'millis':
        return DateTime.fromMillis(input, dtOptions);
      case 'seconds':
      default:
        return DateTime.fromSeconds(input, dtOptions);
    }
  }

  if (input instanceof Date) {
    return DateTime.fromJSDate(input, dtOptions);
  }
}

export function utcToLocalDate(utcDate: DateTime, timeZone?: string): DateTime {
  if (timeZone) {
    return utcDate.setZone(timeZone);
  }

  return utcDate.toLocal();
}

export function isFuture(date: DateTime): boolean {
  return date.diffNow().as('hours') > 0;
}

export function tomorrow(): Date {
  const today = new Date();
  const tomorrow = new Date();

  tomorrow.setDate(today.getDate() + 1);

  return tomorrow;
}

/** Checks if the datetime instance is in post merīdiem (after midday) */
export function isPM(datetime: DateTime): boolean {
  return datetime.hour >= 12;
}

/** Gets the hour from the datetime instance in 12-hour format.
 *  If datetime instance is not valid, returns the default hour that was set.
 */
export function getHourIn12FormatFromDatetime(
  datetime: DateTime | undefined,
  defaultHour: number = 9
): number {
  if (datetime != undefined) {
    if (datetime.hour == 0 || datetime.hour == 12) {
      return 12;
    } else if (isPM(datetime)) {
      return datetime.hour - 12;
    } else {
      return datetime.hour;
    }
  }
  return defaultHour;
}

/** Gets the time format from the datetime instance
 *  If datetime instance is not valid, returns default Time Format that was set.
 *  If default time format was not set, it returns AM.
 */
export function getTimeFormatFromDatetime(
  datetime: DateTime | undefined,
  defaultTimeFormat: TimeFormat = TimeFormat.AM
): TimeFormat {
  if (datetime != undefined) {
    if (isPM(datetime)) {
      return TimeFormat.PM;
    } else {
      return TimeFormat.AM;
    }
  }
  return defaultTimeFormat;
}

/** Gets the time format from the datetime instance
 *  If datetime instance is not valid, returns default date that was set.
 *  If default date was not set, it returns the date tomorrow.
 */
export function getDateFromDatetime(
  datetime: DateTime | undefined,
  defaultDate: Date = tomorrow()
): Date {
  if (datetime != undefined) {
    return new Date(datetime.toString());
  } else {
    return defaultDate;
  }
}

export function getTimestamp() {
  return DateTime.local().minus({ hours: 1 }).toFormat('d LLL');
}

/**
 * Creates a DateTime object from date with the given hour and timezone
 */
export const createHourTz = (
  date: Date,
  timezone: Timezone,
  hour: number
): DateTime => {
  return DateTime.fromObject({
    zone: timezone.value,
  }).set({
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
    hour: hour,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
};

/**
 * Gets the hour in a 24-hour format
 */
export const getHourIn24Format = (
  hour: number,
  timeFormat: TimeFormat
): number => {
  const format = timeFormat === TimeFormat.AM ? 12 : 24;

  let formattedHour: number = hour % format;
  if (timeFormat === TimeFormat.PM && formattedHour != 12) {
    formattedHour += 12;
  }

  return formattedHour;
};

/**
 * Gets the ordinal suffix from the number
 * Ref: https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number/39466341#39466341
 * @param n number
 * @returns string
 */
export const getOrdinalSuffix = (n: number): string => {
  return ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
};

/**
 * Gets the full date string from the Date Time (w/ the Timezone)
 * @param datetime DateTime
 * @returns string
 */
export const getFullDateString = (datetime: DateTime) => {
  const day = parseInt(datetime.toFormat('d'));
  return `${day}${getOrdinalSuffix(day)} ${datetime.toFormat(
    'LLLL y, h:mm a'
  )} (${`UTC ${datetime.toFormat('Z')}:00`}) ${datetime.offsetNameLong}`;
};

export function msToMinuteSeconds(ms?: number): string {
  const date = new Date(ms ?? 0);
  return elapsed(date.getSeconds());
}
